import { useState } from "react";
import Modal from "components/modal";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { getUsageData } from "api/usage";
import useModal from "utils/hooks/use-modal";
import DetailCard from "components/detail-card";
import { useSearchParams } from "react-router-dom";
import { useStripe } from "@stripe/react-stripe-js";
import ShimmerLoader from "components/shimmer-loader";
import PlanDetails from "sections/modal/plan-details";
import { getSubscriptionData } from "api/subscriptions";
import getFormattedDate from "utils/methods/getFormattedDate";
import capitalizeString from "utils/methods/capitalizeString";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";

const Overview = ({ choosePlanLabel = "Choose Plan" }) => {
  const [searchParams] = useSearchParams();
  const planValue = searchParams.get("plan");

  const { openModal, closeModal, isOpen } = useModal(planValue ? true : false);

  // stripe hooks and states
  const stripe = useStripe();
  const [loading, setLoading] = useState(null);

  const { data: userUsageData } = useQuery("userUsage", getUsageData, {
    enabled: false,
  });

  const { data: stripeProducts, isLoading: stripeProductsLoading } = useQuery(
    "stripe-products",
    getAllStripeProducts,
    {
      retry: false,
      keepPreviousData: true,
    }
  );

  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
  } = useQuery("userSubscription", getSubscriptionData, {
    retry: false,
    keepPreviousData: true,
  });

  // The API now returns 200 + null when the user has no active subscription
  // (instead of a 404). `subscriptionData === null` therefore means "loaded,
  // and no subscription" — distinct from undefined (still loading).
  const hasNoSubscription = subscriptionData === null;

  const handleSubmitWithCheckout = async (event, priceId) => {
    event.preventDefault();
    setLoading(priceId);

    try {
      // Call your backend to create the checkout session
      const response = await createCheckoutSession({ priceId });

      // Redirect the user to Stripe Checkout
      const result = await stripe.redirectToCheckout({
        sessionId: response.id,
      });

      if (result.error) {
        toast.error(
          result.error.message || "Failed creating checkout session",
          {
            position: "top-right",
          }
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error processing payment!", {
        position: "top-right",
      });
    } finally {
      setLoading(null);
    }
  };

  const isToBeRenewed = (date) => {
    const currentDate = new Date(Date.now());
    return new Date(date) > currentDate;
  };

  const ifAllDataLoaded = () => {
    return (
      !subscriptionLoading &&
      !stripeProductsLoading &&
      stripeProducts &&
      subscriptionData
    );
  };

  const isSubscriptionActive = () => {
    return (
      !subscriptionLoading &&
      subscriptionData &&
      (subscriptionData.status === "active" ||
        subscriptionData.status === "trialing")
    );
  };

  const isPeriodMonth = () => {
    const allPrices = [];
    Object.keys(stripeProducts).forEach((el) => {
      allPrices.push(...stripeProducts[el]);
    });
    const priceInfo = allPrices.find(
      (el) => el.id === subscriptionData.planDuration.stripePriceId
    );
    return priceInfo.recurring.interval === "month";
  };

  const displayPrice = () => {
    const allPrices = [];
    Object.keys(stripeProducts).forEach((el) => {
      allPrices.push(...stripeProducts[el]);
    });
    const priceInfo = allPrices.find(
      (el) => el.id === subscriptionData.planDuration.stripePriceId
    );
    return (
      priceInfo.currency.toUpperCase() +
      " " +
      priceInfo.unit_amount / 100 +
      "/" +
      priceInfo.recurring.interval
    );
  };

  const getRenewsDisplayText = () => {
    if (subscriptionData.canceledAt) {
      return "Plan ends";
    }

    if (
      subscriptionData.status === "trialing" &&
      isToBeRenewed(subscriptionData.endDate)
    ) {
      return "Trial ends";
    }

    if (
      subscriptionData.status !== "canceled" &&
      isToBeRenewed(subscriptionData.endDate)
    ) {
      return "Next billing date";
    }

    return "Expired on";
  };

  return (
    <>
      <div className="w-full xl:max-w-[850px]">
        <p className="text-base mb-3">Current Plan</p>

        {!ifAllDataLoaded() && !hasNoSubscription && (
          <div className="flex flex-col lg:flex-row gap-5 xl:h-[144px] w-full justify-between mb-[50px]">
            <ShimmerLoader
              className={
                "h-[144px] xl:h-full w-full mb-[50px] rounded-lg overflow-hidden"
              }
            />
            <ShimmerLoader
              className={
                "h-[144px] xl:h-full w-full mb-[50px] rounded-lg overflow-hidden"
              }
            />
          </div>
        )}

        {ifAllDataLoaded() && (
          <div className="flex flex-col xl:flex-row gap-5 w-full justify-between mb-[50px]">
            <DetailCard className={"w-full xl:w-[65%] pb-11 py-2 px-3"}>
              <div className="flex items-center justify-between mb-5 w-full">
                {(subscriptionLoading || stripeProductsLoading) && (
                  <ShimmerLoader className="h-7 w-[100px]" />
                )}
                {ifAllDataLoaded() && (
                  <span className="text-base text-grey">
                    {subscriptionData.planDuration.plan.name} · {isPeriodMonth() ? "Monthly" : "Annual"}
                  </span>
                )}

                {subscriptionLoading && (
                  <ShimmerLoader className="h-[36px] rounded-lg w-28" />
                )}

                {!subscriptionLoading && subscriptionData && (
                  <div
                    className={`${
                      isSubscriptionActive() ? "bg-green" : "bg-red4"
                    } border border-solid border-gray-600 rounded-lg text-base px-[15px] py-2`}
                  >
                    {!isSubscriptionActive() && (
                      <span>{capitalizeString(subscriptionData.status)}</span>
                    )}
                    {isSubscriptionActive() && <span>Active</span>}
                  </div>
                )}
              </div>

              {!ifAllDataLoaded() && <ShimmerLoader className="h-6 w-28" />}

              {ifAllDataLoaded() && (
                <span className="text-base">{displayPrice()}</span>
              )}
            </DetailCard>

            <DetailCard className={"w-full xl:w-[35%] py-2 px-3"}>
              <div className="flex items-center justify-between mb-5">
                {subscriptionLoading && (
                  <ShimmerLoader className="h-7 w-[100px]" />
                )}
                {!subscriptionLoading && subscriptionData && (
                  <span className="text-base text-grey">
                    {getRenewsDisplayText()}
                  </span>
                )}
                <div className="bg-green border border-solid border-black rounded-lg text-base px-[15px] py-2 shadow-secondary-field opacity-0 select-none">
                  Placeholder
                </div>
              </div>
              {subscriptionLoading && (
                <ShimmerLoader className="h-7 w-[200px]" />
              )}
              {!subscriptionLoading && subscriptionData && (
                <p className="text-base mb-1.5">
                  {getFormattedDate(subscriptionData.endDate)}
                </p>
              )}
              {!subscriptionLoading && subscriptionData?.status === "trialing" && (
                <p className="text-xs text-grey">
                  Your {subscriptionData.planDuration.plan.name} billing begins after trial
                </p>
              )}
            </DetailCard>
          </div>
        )}

        {!ifAllDataLoaded() &&
          hasNoSubscription &&
          userUsageData &&
          userUsageData.isFreeTrial && (
            <div className="flex h-auto w-full mb-[30px]">
              <DetailCard className={"w-full xl:w-1/2 pb-11 py-2 px-3"}>
                <div className="flex items-center justify-between mb-5 w-full">
                  <span className="text-base text-grey">Basic</span>

                  <div
                    className={`bg-green border border-solid border-gray-600 rounded-lg text-base px-[5px] py-1`}
                  >
                    <span>Active</span>
                  </div>
                </div>

                <p className="text-base mb-1.5">Free Forever</p>
                <p className="text-sm text-grey">60 sessions / month</p>
              </DetailCard>
            </div>
          )}

        <div className="mb-[50px]">
          <button
            onClick={() => openModal()}
            className="bg-primary border border-solid border-gray-300 shadow-lg hover:shadow-2xl rounded-lg py-2 px-[12px] text-base"
          >
            {choosePlanLabel}
          </button>
        </div>
      </div>

      {isOpen && (
        <Modal isOpen={isOpen} closeModal={closeModal}>
          <PlanDetails
            loading={loading}
            products={stripeProducts}
            userUsageData={userUsageData}
            subscriptionData={subscriptionData}
            handleClickGetStarted={handleSubmitWithCheckout}
          />
        </Modal>
      )}
    </>
  );
};

export default Overview;